import type { ReactNode } from "react";
import { ShieldCheck } from "lucide-react";
import { AdminLegalFooter } from "../AdminLegalFooter.js";
import { VeritrellisLogo } from "../VeritrellisLogo.js";

const trustItems = [
  "ES256 signed permits",
  "Human approval workflows",
  "Product audit trail",
  "JWKS permit verification"
] as const;

type AuthShellProps = {
  children: ReactNode;
  title: string;
  description?: string;
  footer?: ReactNode;
  userEmail?: string | null;
  badge?: string;
};

export const AuthShell = ({
  children,
  title,
  description,
  footer,
  userEmail,
  badge
}: AuthShellProps) => (
  <div className="admin-auth-page">
    <aside className="admin-auth-brand" aria-hidden={false}>
      <div className="admin-auth-brand-top">
        <VeritrellisLogo height={32} />
        <p className="admin-auth-brand-tagline">
          Permit-based control plane for high-risk product and AI-agent actions.
        </p>
      </div>

      <div className="admin-auth-brand-preview">
        <p className="admin-auth-brand-preview-label">How it works</p>
        <ol className="admin-auth-brand-steps">
          <li>
            <span className="admin-auth-brand-step-num">1</span>
            <span>Authorize the action via API or SDK</span>
          </li>
          <li>
            <span className="admin-auth-brand-step-num">2</span>
            <span>Policy returns allow, deny, or pending approval</span>
          </li>
          <li>
            <span className="admin-auth-brand-step-num">3</span>
            <span>Verify the signed permit before execution</span>
          </li>
        </ol>
      </div>

      <ul className="admin-auth-trust-list">
        {trustItems.map((item) => (
          <li key={item}>
            <ShieldCheck className="admin-auth-trust-icon" strokeWidth={1.75} />
            {item}
          </li>
        ))}
      </ul>
    </aside>

    <main className="admin-auth-main">
      <div className="admin-auth-main-inner">
        <div className="admin-auth-mobile-logo">
          <VeritrellisLogo height={28} />
        </div>
        <div className="admin-auth-main-header">
          {badge != null ? <span className="admin-auth-badge">{badge}</span> : null}
          <h1 className="admin-auth-title">{title}</h1>
          {description != null ? <p className="admin-auth-description">{description}</p> : null}
          {userEmail != null && userEmail.length > 0 ? (
            <p className="admin-auth-user-pill" title="Signed in">
              {userEmail}
            </p>
          ) : null}
        </div>

        <div className="admin-auth-card">{children}</div>

        {footer != null ? <div className="admin-auth-footer">{footer}</div> : null}
        <AdminLegalFooter variant="compact" />
      </div>
    </main>
  </div>
);
